import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import toNumber from 'lodash/toNumber';
import {
  Field2 as Field,
  API,
  color,
  MediaFormatHelper,
  VibeCheckbox,
  VibeModal,
} from 'vibeguide';
import Grid from '@mui/material/GridLegacy';

let locationZone = null;

function ProfileInfo({
  className = '',
  name = '',
  companyId = '',
  companyName = '',
  bannerId = '',
  bannerName = '',
  category = '',
  contentRating = '',
  mediaFormat = '',
  zoneId = '',
  zoneName = '',
  impressionMultiplier = null,
  tags = [],
  isAdmin = false,
  canEdit = false,
  salesforceId = '',
  testLocation = false,
  canAssignTag = false,
  canCreateCompanyBanner = false,
  user,
  onUpdate = () => {},
}) {
  const canAdminEdit = isAdmin && canEdit;
  const showContentRating = mediaFormat !== 'visual';

  const [showOnToggleModal, setShowOnToggleModal] = useState(false);
  const [showOffToggleModal, setShowOffToggleModal] = useState(false);
  const [localTestLocation, setLocalTestLocation] = useState(false);
  const [banner, setBanner] = useState({});

  /**
   * When a field value is changed
   */
  const onChange = (e) => {
    const {
      target: {
        name,
        value,
      },
    } = e;

    const includeData = {};

    // when media format is changed (and a location zone is selected) choose the right impression multiplier to use
    if (name === 'mediaFormat' && locationZone) {
      const useImpressionMultiplier = get(locationZone, `${value}Multiplier`) > 0
        ? get(locationZone, `${value}Multiplier`)
        : 1;

      // set impression multiplier when the zone is changed (assume 100% if none is set on the location zone)
      includeData.impressionMultiplier = locationZone
        ? useImpressionMultiplier ?? 1
        : 0;
    }

    onUpdate({
      [name]: value,
      ...includeData,
    });
  };

  /**
   * When the impression multiplier is changed
   */
  const onChangeImpressionMultiplier = (e) => {
    const {
      target: {
        name,
        value,
      },
    } = e;

    onUpdate({
      [name]: toNumber(value) / 100,
    });
  };

  /**
   * When the tag field is changed
   */
  const onChangeTag = (data) => {
    onUpdate(data);
  };

  const confirmToggleTestLocation = () => {
    if (showOnToggleModal) {
      setShowOnToggleModal(false);
    } else {
      setShowOffToggleModal(false);
    }

    onUpdate({
      testLocation: localTestLocation,
    });
  };

  const closeOnToggleTestLocationModal = () => {
    setShowOnToggleModal(false);
  };

  const closeOffToggleTestLocationModal = () => {
    setShowOffToggleModal(false);
  };

  const onToggleTestLocation = (e) => {
    const {
      checked,
    } = e;

    if (!testLocation) {
      setShowOnToggleModal(true);
    } else {
      setShowOffToggleModal(true);
    }

    setLocalTestLocation(checked);
  };

  /**
   * When a dropdown is changed
   */
  const onChangeDropdown = ({
    id,
    name,
    value,
    data,
  }) => {
    const includeData = {};

    if (name === 'zoneName') {
      // include zone ID if changing the location zone
      includeData.zoneId = id;

      // save location zone
      locationZone = data;

      const useImpressionMultiplier = get(locationZone, `${mediaFormat}Multiplier`) > 0
        ? get(locationZone, `${mediaFormat}Multiplier`)
        : 1;

      // set impression multiplier when the zone is changed (assume 100% if none is set on the location zone)
      includeData.impressionMultiplier = locationZone
        ? useImpressionMultiplier ?? 1
        : 0;
    } else if (name === 'bannerName') {
      // include banner ID if changing the location banner
      includeData.bannerId = id;

      // remove the location zone / impression multiplier when the banner is changed
      includeData.zoneId = '';
      includeData.zoneName = '';
      includeData.impressionMultiplier = 0;
    }

    onUpdate({
      [name]: value,
      ...includeData,
    });
  };

  /**
   * Fetch banner details
   */
  useEffect(() => {
    if (!bannerId || !companyId) {
      return;
    }

    async function fetchData() {
      const response = await API.Company.Banner.get({
        _id: bannerId,
        companyId,
      });

      setBanner(response);
    }
    fetchData();
  }, [bannerId]);

  return (
    <div className={classNames('ProfileInfo', className)}>
      <Grid spacing={2} container>
        <Grid
          xs={6}
          lg={isAdmin
            ? 6
            : 12}
          item
        >
          <Field
            type="text"
            label="Location Name"
            placeholder="Location Name"
            name="name"
            value={name}
            tabIndex={1}
            onChange={onChange}
            disabled={!canAdminEdit}
            required
          />
        </Grid>

        {/* <Grid
          xs={6}
          lg={isAdmin
            ? 6
            : 12}
          item
        >
          <Field
            type="text"
            label="Industry"
            value={companyName}
            tabIndex={2}
            companyProps={{
              companyId,
              companyName,
            }}
            required
          />
        </Grid> */}

        {isAdmin && (
          <Grid
            xs={6}
            lg={isAdmin
              ? 6
              : 12}
            item
          >
            <Field
              type="company"
              label="Company"
              value={companyName}
              tabIndex={3}
              companyProps={{
                companyId,
                companyName,
              }}
              disabled
              required
            />
          </Grid>
        )}

        <Grid
          xs={6}
          lg={isAdmin
            ? 6
            : 12}
          item
        >
          <Field
            type="dropdown"
            label="Category"
            name="category"
            value={category}
            tabIndex={4}
            dropdownProps={{
              type: 'category',
              attr: 'value',
              fetch: API.Company.Category.list,
              create: API.Company.Category.create,
              canCreate: user.can('company_category.create'),
              successMessage: 'COMPANYCATEGORY.CREATED',
            }}
            disabled={!canAdminEdit}
            onChange={onChangeDropdown}
            required
          />
        </Grid>

        <Grid
          xs={6}
          lg={isAdmin
            ? 6
            : 12}
          item
        >
          <Field
            type="dropdown"
            label="Location Banner"
            name="bannerName"
            value={bannerName}
            tabIndex={5}
            dropdownProps={{
              type: 'banner',
              attr: 'value',
              companyId,
              fetch: API.Company.Banner.list,
              create: API.Company.Banner.create,
              canCreate: canCreateCompanyBanner,
              successMessage: 'COMPANYBANNER.CREATED',
            }}
            disabled={!canAdminEdit}
            onChange={onChangeDropdown}
            required
          />
        </Grid>

        <Grid xs={12} lg={!showContentRating ? 12 : 6} item>
          {/* Media Format can only be set on location create/import */}
          <div
            style={{ width: !showContentRating ? 'calc(50% - 8px)' : 'auto' }}
          >
            <Field
              type="select"
              label="Media Format"
              name="mediaFormat"
              value={mediaFormat}
              options={MediaFormatHelper.list({ includeEmpty: true })}
              disabled
              tabIndex={14}
              onChange={onChange}
            />
          </div>
        </Grid>

        {showContentRating && (
          <Grid xs={12} lg={6} item>
            <Field
              type="select"
              label="Maximum Content Rating"
              name="contentRating"
              value={contentRating}
              tabIndex={15}
              options={[
                {
                  label: 'Radio Edit',
                  value: 'radio-edit',
                },
                {
                  label: 'G',
                  value: 'g',
                },
                {
                  label: 'Explicit',
                  value: 'explicit',
                },
              ]}
              disabled={!canEdit}
              onChange={onChange}
            />
          </Grid>
        )}

        <Grid xs={12} lg={6} item>
          <Field
            type="dropdown"
            label="Location Zone"
            placeholder="Search for a Location Zone..."
            name="zoneName"
            value={zoneName || ''}
            dropdownProps={{
              type: 'collection',
              attr: 'name',
              collection: banner.locationZones,
            }}
            disabled={!canAdminEdit || !bannerId || !mediaFormat}
            onChange={onChangeDropdown}
            tabIndex={16}
          />
        </Grid>

        <Grid xs={12} lg={6} item>
          <Field
            type="number"
            label="Impression Multiplier"
            name="impressionMultiplier"
            placeholder="Enter value between 1-100"
            value={impressionMultiplier || ''}
            tabIndex={17}
            numberProps={{
              min: 0,
              max: 100,
              inputGroupRight: '%',
            }}
            disabled={!canAdminEdit || !zoneId}
            onChange={onChangeImpressionMultiplier}
          />
        </Grid>

        {canEdit && (
          <Grid xs={12} lg={6} item>
            <Field
              type="text"
              label="Salesforce ID"
              placeholder="XXXXXXXXXXXXXXXXXX"
              name="salesforceId"
              value={salesforceId}
              tabIndex={18}
              disabled={!canAdminEdit}
              onChange={onChange}
            />
          </Grid>
        )}

        <Grid xs={12} lg={12} item>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
            className="toggle-container"
          >
            <VibeCheckbox
              label="Test Location"
              color={color.primary}
              name="testLocation"
              checked={testLocation}
              onChange={onToggleTestLocation}
            />
          </div>
        </Grid>

        {/* <Grid
          xs={6}
          lg={isAdmin
            ? 6
            : 12}
          item
        >
          <Field
            type="select"
            label="Business Status"
            name="companyName"
            value={companyName}
            options={[
              {
                label: '',
                value: '',
              },
              {
                label: 'Audio',
                value: 'audio',
              },
              {
                label: 'Visual',
                value: 'visual',
              },
            ]}
            tabIndex={6}
            onChange={onChange}
            required
          />
        </Grid> */}

        <Grid xs={12} item>
          <Field
            type="tags"
            label="Tags"
            name="tags"
            tagProps={{
              companyId,
              tags,
              allowTypes: ['client', 'admin'],
              showAs: 'list',
              assign: canAssignTag,
            }}
            disabled={!canEdit}
            onChange={onChangeTag}
            tabIndex={19}
          />
        </Grid>
      </Grid>

      <VibeModal
        show={showOnToggleModal}
        type="confirm"
        title="Test Location"
        text="You are marking this as a test location, excluding it from reporting."
        onConfirm={confirmToggleTestLocation}
        onClose={closeOnToggleTestLocationModal}
      />

      <VibeModal
        show={showOffToggleModal}
        type="confirm"
        title="Test Location"
        text="You are removing this as a test location. This location will now show in reporting."
        onConfirm={confirmToggleTestLocation}
        onClose={closeOffToggleTestLocationModal}
      />
    </div>
  );
}

ProfileInfo.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string,
  companyId: PropTypes.string,
  companyName: PropTypes.string,
  bannerId: PropTypes.string,
  bannerName: PropTypes.string,
  category: PropTypes.string,
  contentRating: PropTypes.string,
  mediaFormat: PropTypes.string,
  zoneId: PropTypes.string,
  zoneName: PropTypes.string,
  impressionMultiplier: PropTypes.number,
  salesforceId: PropTypes.string,
  testLocation: PropTypes.bool,
  tags: PropTypes.arrayOf(PropTypes.object),
  isAdmin: PropTypes.bool,
  canEdit: PropTypes.bool,
  canAssignTag: PropTypes.bool,
  onUpdate: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

export default connect(mapStateToProps)(ProfileInfo);
